import BookIcon from "@mui/icons-material/Book";
import {
    AccountTree as AccountTreeIcon,
    ArrowBack as ArrowBackIcon,
    AutoAwesome as AutoAwesomeIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    KeyboardReturn as KeyboardReturnIcon,
    MenuBook as MenuBookIcon,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    Paper,
    Popover,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    WORD_REVEAL_BANK_BOUNCE_MS,
    wordBankLockInKeyframes,
} from "../../common/components/WordRevealFx";
import WordNetworkPanel from "../../../components/puzzle/WordNetworkPanel";
import StoryClueNumberCircle from "../../../story/StoryClueNumberCircle";
import AuthService from "../../../../services/AuthService";
import LoginConfirmDialog from "../../../components/LoginConfirmDialog";
import {
    findFilledCoordByWord,
    getFoundCellKeySetFromWords,
    getHintPatternForWordCells,
    hintPatternHasRevealedLetter,
    normalizeWord,
    removeHyphens
} from "../../common/utils/PuzzlePlayLogic";
import HintPatternBoxes from "./HintPatternBoxes";
import UtilService from "../../../../services/UtilService";
import SubscriptionDialog from "../../../subscription/SubscriptionDialog";
import WordMeaningDialog from "../../common/WordMeaningDialog";
import { usePagesTranslation } from "../../../locales";
import AssistantCreditsEmptyBar from "../../common/components/AssistantCreditsEmptyBar";
import { ACCENT, SURFACE, TEXT } from "../../../../dark-theme/palette";
import { playPillButtonSx } from "../../../../dark-theme/surfaces";

const MAX_MEANING_PREVIEW_LEN = 150;

/** Omit server-stored system prompts (e.g. “You are an admin…”) from the clues-panel chat transcript. */
function shouldShowAssistantHistoryMessage(m) {
    const c = String(m?.content ?? "").trim();
    if (!c) return false;
    const role = String(m?.role ?? "").toUpperCase();
    if (/you are an admin/i.test(c)) return false;
    if (role === "USER" && /^you are\b/i.test(c) && c.length >= 80) return false;
    return true;
}

const CrossWordCluesPanel = ({
    filledCoordinates = [],
    wordsFound = [],
    onHide,
    selectedWord = null,
    selectedWords = [],
    onWordSelect,
    onWordEnterSubmit,
    hideAllWords = false,
    userPayPlan = null,
    language = 'english',
    tags = [],
    crossTplId = null,
    maxWords = null,
    useCacheEndpoint = false,
    /** From GET /home/cross/:id — `{ word, definition }[]`; shown in Word Clues when present. */
    displayClue = null,
    /** Grid cells where a hint letter is shown (same as CrossPlayMain CrossGrid); empty when hints off */
    hintRevealCells = [],
    /** Word network graph from template (optional); shown in panel after puzzle is complete when toggled */
    wordNetwork = null,
    isPuzzleComplete = false,
    /** When true, replace clues list with Chat with AI (history + input). */
    chatWithAiOpen = false,
    /** When set, controls magic-button visibility (overrides inferring from onOpenChatWithAi alone). */
    assistantMagicVisible: assistantMagicVisibleProp,
    /** Tooltip on the magic icon (e.g. sign-in hint for guests). */
    assistantMagicTooltipTitle,
    /** When true, chat input and Send are disabled and locked text is shown in the field. */
    assistantInputLocked = false,
    assistantLockedInputText = "",
    onOpenChatWithAi,
    onOpenStory,
    /** Story accordion rendered above Across / Down (from CrossPlayMain). */
    storySlot = null,
    /** Pulse when a revealed word lands in the bank. */
    bankLockIn = false,
    onCloseChatWithAi = () => {},
    assistantMessages = [],
    assistantPrompt = "",
    onAssistantPromptChange = () => {},
    onAssistantSend = () => {},
    assistantLoading = false,
    assistantHistoryLoading = false,
    chatWithAiTitle = "Chat with AI",
    chatWithAiPlaceholder = "",
    /** From `/home/user` — when `0` and user can chat, show empty-credits UI and disable send. */
    assistantCreditsTotal = undefined
}) => {
    const { t, lang: uiLang } = usePagesTranslation();
    const navigate = useNavigate();
    const showAssistantMagicButton =
        assistantMagicVisibleProp != null
            ? Boolean(assistantMagicVisibleProp)
            : typeof onOpenChatWithAi === "function";
    const [expandedMeanings, setExpandedMeanings] = useState(new Set());
    const [selectedWordForMeaning, setSelectedWordForMeaning] = useState(selectedWord);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogWord, setDialogWord] = useState(null);
    const [showFoundWords] = useState(false); // Toggle to show/hide found words
    const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
    const [enterPopoverAnchor, setEnterPopoverAnchor] = useState(null);
    const [enterPopoverWord, setEnterPopoverWord] = useState(null);
    const [enterPopoverNumber, setEnterPopoverNumber] = useState(null);
    const [enterPopoverValue, setEnterPopoverValue] = useState("");
    const [localEnterPopoverValue, setLocalEnterPopoverValue] = useState("");
    const [showWordNetwork, setShowWordNetwork] = useState(false);
    const [networkLoginDialogOpen, setNetworkLoginDialogOpen] = useState(false);
    const enterWordInputRef = useRef(null);

    const hasWordNetwork = useMemo(
        () => Boolean(wordNetwork?.nodes?.length && wordNetwork?.links?.length),
        [wordNetwork]
    );

    const assistantCreditsDepleted =
        !assistantInputLocked &&
        typeof assistantCreditsTotal === "number" &&
        assistantCreditsTotal <= 0;

    const visibleAssistantMessages = useMemo(
        () =>
            Array.isArray(assistantMessages)
                ? assistantMessages.filter(shouldShowAssistantHistoryMessage)
                : [],
        [assistantMessages]
    );

    const displayClueDefinitionByWord = useMemo(() => {
        const list = Array.isArray(displayClue) ? displayClue : [];
        const map = new Map();
        for (const entry of list) {
            const w = entry?.word;
            const def = String(entry?.definition ?? "").trim();
            if (!w || !def) continue;
            const k = normalizeWord(String(w));
            if (!k) continue;
            map.set(k, def);
            const kh = removeHyphens(k);
            if (kh && kh !== k) {
                map.set(kh, def);
            }
        }
        return map;
    }, [displayClue]);

    const resolveDisplayClueDefinition = useCallback(
        (word) => {
            if (!word) return "";
            const k = normalizeWord(word);
            return (
                displayClueDefinitionByWord.get(k) ||
                displayClueDefinitionByWord.get(removeHyphens(k)) ||
                ""
            );
        },
        [displayClueDefinitionByWord]
    );

    useEffect(() => {
        if (!isPuzzleComplete) {
            setShowWordNetwork(false);
        }
    }, [isPuzzleComplete]);

    const handleWordNetworkToggle = async () => {
        const isAuthenticated = await AuthService.isAuthenticated();
        if (!isAuthenticated) {
            setNetworkLoginDialogOpen(true);
            return;
        }
        setShowWordNetwork((prev) => !prev);
    };

    const debouncedSetEnterPopoverValue = useMemo(
        () =>
            debounce((value) => {
                setEnterPopoverValue(value);
            }, 300),
        []
    );

    const foundCellKeySet = useMemo(
        () => getFoundCellKeySetFromWords(filledCoordinates, wordsFound),
        [filledCoordinates, wordsFound]
    );

    const enterPopoverHintPattern = useMemo(() => {
        if (!enterPopoverWord) return null;
        const coord = findFilledCoordByWord(filledCoordinates, enterPopoverWord);
        if (!coord?.positions?.length) return null;
        const wordCells = coord.positions.map((p) => ({ row: p.row, col: p.col }));
        return getHintPatternForWordCells(enterPopoverWord, wordCells, hintRevealCells, foundCellKeySet);
    }, [enterPopoverWord, filledCoordinates, hintRevealCells, foundCellKeySet]);

    const toggleMeaningExpanded = (key) => {
        setExpandedMeanings(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    // Get the first character position of a word
    const getFirstPosition = (coord) => {
        if (!coord.positions || coord.positions.length === 0) return null;

        // Exclude hidden message words
        if (coord.direction === "scattered") {
            return null;
        }

        let firstPos;
        
        if (coord.reversed) {
            // Reversed word: first character of word text is at the last position in positions array
            firstPos = coord.positions[coord.positions.length - 1];
        } else {
            // Normal word: get first position based on direction
            if (coord.direction === "vertical") {
                firstPos = coord.positions.reduce((min, pos) => pos.row < min.row ? pos : min);
            } else if (coord.direction === "horizontal") {
                firstPos = coord.positions.reduce((min, pos) => pos.col < min.col ? pos : min);
            } else {
                // For diagonal directions, use the first position in the array
                firstPos = coord.positions[0];
            }
        }

        return firstPos;
    };

    // Calculate word numbers: assign numbers to words based on their starting positions
    // Words are ordered left-to-right, top-to-bottom
    // Words that share the same starting cell get the same number
    const wordNumbers = useMemo(() => {
        if (!filledCoordinates || filledCoordinates.length === 0) {
            return new Map(); // Map of "row,col" -> number
        }

        // Collect all unique starting positions
        const startingPositions = new Map(); // Map of "row,col" -> {row, col}
        
        filledCoordinates.forEach(coord => {
            // Skip hidden message words
            if (coord.direction === "scattered") {
                return;
            }

            const firstPos = getFirstPosition(coord);
            if (!firstPos) return;

            const cellKey = `${firstPos.row},${firstPos.col}`;
            
            // Store the position if not already stored
            if (!startingPositions.has(cellKey)) {
                startingPositions.set(cellKey, { row: firstPos.row, col: firstPos.col });
            }
        });

        // Sort positions: top-to-bottom (by row), then left-to-right (by column)
        const sortedPositions = Array.from(startingPositions.values()).sort((a, b) => {
            if (a.row !== b.row) {
                return a.row - b.row; // Top to bottom
            }
            return a.col - b.col; // Left to right
        });

        // Assign numbers to sorted positions
        const cellToNumber = new Map();
        sortedPositions.forEach((pos, index) => {
            const cellKey = `${pos.row},${pos.col}`;
            cellToNumber.set(cellKey, index + 1); // Start from 1
        });

        return cellToNumber;
    }, [filledCoordinates]);

    // Get word number for a specific coordinate
    const getWordNumberForCoord = (coord) => {
        const firstPos = getFirstPosition(coord);
        if (!firstPos) return null;
        const cellKey = `${firstPos.row},${firstPos.col}`;
        return wordNumbers.get(cellKey) || null;
    };

    // Organize words by direction (across = horizontal, down = vertical)
    const { acrossWords, downWords } = useMemo(() => {
        if (!filledCoordinates || filledCoordinates.length === 0) {
            return { acrossWords: [], downWords: [] };
        }

        const across = [];
        const down = [];

        filledCoordinates.forEach(coord => {
            if (!coord.text) return;

            const wordData = {
                text: coord.text,
                id: coord.id,
                length: coord.length,
                positions: coord.positions,
                direction: coord.direction,
                reversed: coord.reversed,
                number: getWordNumberForCoord(coord)
            };

            if (coord.direction === 'horizontal') {
                across.push(wordData);
            } else if (coord.direction === 'vertical') {
                down.push(wordData);
            }
        });

        // Sort words by their number (words with same starting cell will have same number)
        // Then alphabetically for words with same number
        across.sort((a, b) => {
            if (a.number !== b.number) {
                return (a.number || 999) - (b.number || 999);
            }
            return a.text.localeCompare(b.text);
        });
        down.sort((a, b) => {
            if (a.number !== b.number) {
                return (a.number || 999) - (b.number || 999);
            }
            return a.text.localeCompare(b.text);
        });

        return { acrossWords: across, downWords: down };
    }, [filledCoordinates, wordNumbers]);

    // Update selected word for meaning when prop changes
    useEffect(() => {
        setSelectedWordForMeaning(selectedWord);
    }, [selectedWord]);

    // Clear selectedWordForMeaning when selectedWords changes (new cell clicked)
    // This ensures previous selections are cleared when a new cell is clicked
    useEffect(() => {
        // Only clear if selectedWord prop is null (not explicitly set by user clicking word in panel)
        if (!selectedWord && selectedWordForMeaning) {
            // Check if the current selectedWordForMeaning is in the new selectedWords
            const isStillSelected = selectedWords.some(sw => 
                sw.toLowerCase().trim() === selectedWordForMeaning.toLowerCase().trim()
            );
            // If not in the new selection, clear it
            if (!isStillSelected) {
                setSelectedWordForMeaning(null);
            }
        }
    }, [selectedWords, selectedWord]);

    const handleWordClick = (word) => {
        setSelectedWordForMeaning(word);
        if (onWordSelect) {
            onWordSelect(word);
        }
    };

    const handleInfoClick = (event, word) => {
        event.stopPropagation(); // Prevent triggering the word click
        
        // Check if user has Standard (paid) access
        const hasStandardPlan = UtilService.isStandardPayPlan(userPayPlan);

        if (!hasStandardPlan) {
            // Show subscription dialog for FREE users
            setSubscriptionDialogOpen(true);
        } else {
            // Show word meaning dialog for Standard subscribers
            setDialogWord(word);
            setDialogOpen(true);
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setDialogWord(null);
    };

    const handleEnterIconClick = (event, word, number) => {
        event.stopPropagation();
        debouncedSetEnterPopoverValue.cancel();
        setEnterPopoverAnchor(event.currentTarget);
        setEnterPopoverWord(word);
        setEnterPopoverNumber(number ?? null);
        setEnterPopoverValue("");
        setLocalEnterPopoverValue("");
    };

    const handleEnterPopoverClose = () => {
        debouncedSetEnterPopoverValue.cancel();
        setEnterPopoverAnchor(null);
        setEnterPopoverWord(null);
        setEnterPopoverNumber(null);
        setEnterPopoverValue("");
        setLocalEnterPopoverValue("");
    };

    const handleEnterPopoverCheck = () => {
        if (enterPopoverWord && onWordEnterSubmit) {
            debouncedSetEnterPopoverValue.flush();
            const valueToSubmit = localEnterPopoverValue || enterPopoverValue;
            const closePopover = onWordEnterSubmit(enterPopoverWord, valueToSubmit);
            if (closePopover === false) {
                return;
            }
        }
        handleEnterPopoverClose();
    };

    useEffect(() => {
        return () => debouncedSetEnterPopoverValue.cancel();
    }, [debouncedSetEnterPopoverValue]);

    const isWordFound = (word) => {
        if (!word || !wordsFound || wordsFound.length === 0) return false;
        const normalizedWord = word.toLowerCase().trim();
        return wordsFound.some(foundWord => {
            const foundWordNormalized = typeof foundWord === 'string' ? foundWord.toLowerCase().trim() : String(foundWord).toLowerCase().trim();
            return foundWordNormalized === normalizedWord;
        });
    };

    const renderWordList = (words, title) => {
        if (words.length === 0) {
            return null;
        }

        // Check if all words in this section are found
        const allWordsFound = words.length > 0 && words.every(wordData => isWordFound(wordData.text));

        // When all found, show full list. Otherwise filter based on showFoundWords toggle
        const filteredWords = allWordsFound
            ? words
            : (showFoundWords ? words : words.filter(wordData => !isWordFound(wordData.text)));

        return (
            <Box sx={{ mb: 1.25 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25, color: ACCENT.light, fontSize: '0.8rem' }}>
                    {title} ({words.length})
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {filteredWords.map((wordData, index) => {
                        const word = wordData.text;
                        const found = isWordFound(word);
                        const showAsFound = found && !allWordsFound; // When showing full list, use original (unfound) styling
                        const displayDefText = resolveDisplayClueDefinition(word);
                        
                        // If hideAllWords is true and word is found and showFoundWords is false, skip rendering this item entirely
                        // When showFoundWords is true or allWordsFound (show full list), show all words
                        if (hideAllWords && found && !showFoundWords && !allWordsFound) {
                            return null;
                        }
                        
                        const hasMeaning = Boolean(displayDefText);
                        const shouldShowMeaning = showFoundWords ? hasMeaning : (!found && hasMeaning) || (allWordsFound && hasMeaning);
                        
                        // Check if this word is in the selectedWords array (normalized comparison)
                        const isSelected = selectedWords.some(sw => 
                            sw.toLowerCase().trim() === word.toLowerCase().trim()
                        ) || selectedWordForMeaning === word;

                        const showWordChip = (!hideAllWords || allWordsFound);
                        const clueColumnWidth = showWordChip ? '5.25rem' : '2.25rem';

                        return (
                            <Box 
                                key={`${wordData.id || index}`} 
                                sx={{ 
                                    p: 0.5,
                                    borderRadius: 1,
                                    backgroundColor: isSelected ? ACCENT.soft : 'transparent',
                                    border: isSelected ? '1px solid #3BA666' : '1px solid transparent',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    '&:hover .clue-row-actions, &:focus-within .clue-row-actions': {
                                        opacity: 1,
                                        pointerEvents: 'auto',
                                    },
                                    '&:hover, &:focus-within': {
                                        backgroundColor: isSelected ? ACCENT.soft : SURFACE.hover,
                                    },
                                }}
                                onClick={() => handleWordClick(word)}
                            >
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    flex: 1,
                                    minWidth: 0,
                                    position: 'relative',
                                }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 0.35,
                                            flexShrink: 0,
                                            width: clueColumnWidth,
                                            minWidth: clueColumnWidth,
                                            maxWidth: clueColumnWidth,
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        {wordData.number && (!found || showFoundWords || allWordsFound) && (
                                            <StoryClueNumberCircle>
                                                {wordData.number}
                                            </StoryClueNumberCircle>
                                        )}
                                        {(!hideAllWords || allWordsFound) && (
                                            <Chip
                                                label={word}
                                                variant={showAsFound ? "filled" : "outlined"}
                                                color={showAsFound ? "success" : "default"}
                                                size="small"
                                                sx={{
                                                    backgroundColor: showAsFound ? '#FFEB3B' : undefined,
                                                    textDecoration: showAsFound ? 'line-through' : 'none',
                                                    opacity: showAsFound ? 0.9 : 1,
                                                    fontWeight: showAsFound ? 'bold' : 'normal',
                                                    color: showAsFound ? '#333' : undefined,
                                                    flexShrink: 0,
                                                    m: 0,
                                                    height: 'auto',
                                                    maxWidth: '100%',
                                                    '& .MuiChip-label': {
                                                        fontWeight: showAsFound ? 'bold' : 'normal',
                                                        color: showAsFound ? '#333' : undefined,
                                                        px: 0.75,
                                                        whiteSpace: 'normal',
                                                        textAlign: 'center',
                                                        lineHeight: 1.2
                                                    },
                                                    '&.MuiChip-filled': {
                                                        backgroundColor: showAsFound ? '#FFEB3B !important' : undefined,
                                                        color: showAsFound ? '#333 !important' : undefined
                                                    }
                                                }}
                                            />
                                        )}
                                    </Box>
                                        {shouldShowMeaning && displayDefText ? (
                                            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                {(() => {
                                                        const meaningKey = `${wordData.id || index}-display`;
                                                        const isExpanded = expandedMeanings.has(meaningKey);
                                                        const isLong = displayDefText.length > MAX_MEANING_PREVIEW_LEN;
                                                        const displaySlice =
                                                            isLong && !isExpanded
                                                                ? displayDefText.substring(0, MAX_MEANING_PREVIEW_LEN)
                                                                : displayDefText;
                                                        return (
                                                            <Typography
                                                                variant="body2"
                                                                component="span"
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                    fontStyle: 'italic',
                                                                    lineHeight: 1.4,
                                                                    m: 0
                                                                }}
                                                            >
                                                                {displaySlice}
                                                                {isLong && !isExpanded && (
                                                                    <Box
                                                                        component="span"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleMeaningExpanded(meaningKey);
                                                                        }}
                                                                        sx={{
                                                                            color: 'primary.main',
                                                                            cursor: 'pointer',
                                                                            fontWeight: 600,
                                                                            ml: 0.25,
                                                                            '&:hover': { textDecoration: 'underline' }
                                                                        }}
                                                                    >
                                                                        ...
                                                                    </Box>
                                                                )}
                                                            </Typography>
                                                        );
                                                    })()}
                                            </Box>
                                        ) : null}
                                        <Box
                                            className="clue-row-actions"
                                            sx={{
                                                position: 'absolute',
                                                right: 4,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 0.25,
                                                opacity: 0,
                                                pointerEvents: 'none',
                                                transition: 'opacity 0.2s ease',
                                                zIndex: 2,
                                            }}
                                        >
                                            <Tooltip
                                                title={t(
                                                    "puzzle.playCross.clue-dictionary-tooltip",
                                                    "Dictionary"
                                                )}
                                                arrow
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleInfoClick(e, word)}
                                                    aria-label={t(
                                                        "puzzle.playCross.clue-dictionary-tooltip",
                                                        "Dictionary"
                                                    )}
                                                    sx={{
                                                        ...playPillButtonSx,
                                                        color: ACCENT.light,
                                                        p: 0.25,
                                                        '&:hover': {
                                                            backgroundColor: ACCENT.soft,
                                                            color: TEXT.primary
                                                        }
                                                    }}
                                                >
                                                    <BookIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={`Enter ${removeHyphens(word || '').length} letter word`} arrow>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleEnterIconClick(e, word, wordData.number)}
                                                    sx={{
                                                        ...playPillButtonSx,
                                                        color: ACCENT.light,
                                                        p: 0.25,
                                                        '&:hover': {
                                                            backgroundColor: ACCENT.soft,
                                                            color: TEXT.primary
                                                        }
                                                    }}
                                                >
                                                    <KeyboardReturnIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    };

    return (
        <Paper
            elevation={3}
            data-word-bank
            sx={{
                width: '100%',
                height: '100%',
                backgroundColor: SURFACE.card,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                ...(bankLockIn
                    ? {
                          animation: `${wordBankLockInKeyframes} ${WORD_REVEAL_BANK_BOUNCE_MS}ms ease-out`,
                      }
                    : {}),
            }}
        >
            {/* Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1,
                borderBottom: `1px solid ${SURFACE.border}`,
                gap: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
                    {chatWithAiOpen ? (
                        <>
                            <Tooltip title={t("puzzle.playCross.back-to-clues", "Back to word clues")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={onCloseChatWithAi}
                                    aria-label={t("puzzle.playCross.back-to-clues", "Back to word clues")}
                                    sx={{
                                        color: ACCENT.light,
                                        flexShrink: 0,
                                        '&:hover': {
                                            backgroundColor: ACCENT.soft,
                                            color: TEXT.primary,
                                        },
                                    }}
                                >
                                    <ArrowBackIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ACCENT.light, fontSize: '0.95rem' }}>
                                {chatWithAiTitle}
                            </Typography>
                        </>
                    ) : (
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, color: ACCENT.light, fontSize: "0.95rem", minWidth: 0 }}
                        >
                            {t("puzzle.playCross.word-clues")}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                    {!chatWithAiOpen && showAssistantMagicButton ? (
                        <Tooltip
                            title={
                                assistantMagicTooltipTitle ||
                                t("puzzle.playCross.ai-chat-tooltip", "Open Chat with AI in the word list")
                            }
                            arrow
                        >
                            <span>
                                <IconButton
                                    onClick={() => onOpenChatWithAi?.()}
                                    size="small"
                                    aria-label={t("puzzle.playCross.chat-with-ai", "Chat with AI")}
                                    sx={{
                                        backgroundColor: "secondary.main",
                                        color: "#fff",
                                        width: 28,
                                        height: 28,
                                        minWidth: 28,
                                        minHeight: 28,
                                        borderRadius: "50%",
                                        flexShrink: 0,
                                        "&:hover": {
                                            backgroundColor: "secondary.dark",
                                            color: "#fff",
                                        },
                                    }}
                                >
                                    <AutoAwesomeIcon sx={{ fontSize: 16, color: "#fff" }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : null}
                    {!chatWithAiOpen && typeof onOpenStory === "function" ? (
                        <Tooltip
                            title={t("puzzle.playCross.open-story-tooltip", "Read story")}
                            arrow
                        >
                            <IconButton
                                onClick={onOpenStory}
                                size="small"
                                aria-label={t("puzzle.playCross.open-story-tooltip", "Read story")}
                                sx={{
                                    color: TEXT.secondary,
                                    flexShrink: 0,
                                    "&:hover": {
                                        backgroundColor: SURFACE.hover,
                                        color: TEXT.primary,
                                    },
                                }}
                            >
                                <MenuBookIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ) : null}
                    {!chatWithAiOpen && isPuzzleComplete && hasWordNetwork && (
                        <Tooltip
                            title={
                                showWordNetwork
                                    ? t("puzzle.playCross.hide-network")
                                    : t("puzzle.playCross.show-network")
                            }
                            arrow
                        >
                            <IconButton
                                onClick={handleWordNetworkToggle}
                                size="small"
                                aria-pressed={showWordNetwork}
                                sx={{
                                    color: showWordNetwork ? ACCENT.light : TEXT.secondary,
                                    backgroundColor: showWordNetwork ? ACCENT.soft : "transparent",
                                    flexShrink: 0,
                                    "&:hover": {
                                        backgroundColor: showWordNetwork
                                            ? "rgba(59, 166, 102, 0.28)"
                                            : SURFACE.hover,
                                        color: showWordNetwork ? ACCENT.light : TEXT.primary,
                                    },
                                }}
                            >
                                <AccountTreeIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Close" arrow>
                        <IconButton
                            onClick={onHide || (() => {})}
                            size="small"
                            sx={{
                                color: TEXT.secondary,
                                "&:hover": {
                                    backgroundColor: SURFACE.hover,
                                    color: TEXT.primary,
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {chatWithAiOpen ? (
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            p: 1.25,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        {assistantHistoryLoading && assistantMessages.length === 0 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : null}
                        {visibleAssistantMessages.map((m) => {
                            const isUser = String(m.role).toUpperCase() === 'USER';
                            return (
                                <Box
                                    key={m.id}
                                    sx={{
                                        alignSelf: isUser ? 'flex-end' : 'flex-start',
                                        maxWidth: '92%',
                                        px: 1.25,
                                        py: 0.75,
                                        borderRadius: 2,
                                        bgcolor: isUser ? '#d4edda' : SURFACE.sunken,
                                        color: isUser ? '#155724' : TEXT.primary,
                                    }}
                                >
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {m.content}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                    <Box
                        sx={{
                            flexShrink: 0,
                            p: 1.25,
                            borderTop: `1px solid ${SURFACE.border}`,
                            backgroundColor: SURFACE.sunken,
                        }}
                    >
                        {assistantCreditsDepleted ? (
                            <Box sx={{ mb: 1.25 }}>
                                <AssistantCreditsEmptyBar
                                    message={t(
                                        "puzzle.playCross.assistant-credits-empty",
                                        "You're out of Coins. Add a subscription or coin pack to keep using the assistant."
                                    )}
                                    linkLabel={t(
                                        "puzzle.playCross.assistant-credits-empty-link",
                                        "View plans and coin packs"
                                    )}
                                />
                            </Box>
                        ) : null}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                multiline={false}
                                placeholder={
                                    chatWithAiPlaceholder ||
                                    t("puzzle.playCross.assistant-placeholder", "Ask for a hint about this puzzle")
                                }
                                value={assistantInputLocked ? assistantLockedInputText : assistantPrompt}
                                onChange={(e) => {
                                    if (assistantInputLocked || assistantCreditsDepleted) return;
                                    onAssistantPromptChange(e.target.value);
                                }}
                                disabled={assistantLoading || assistantInputLocked || assistantCreditsDepleted}
                                onKeyDown={(e) => {
                                    if (assistantInputLocked || assistantCreditsDepleted) return;
                                    if (e.key !== "Enter" || e.shiftKey) return;
                                    if (e.nativeEvent.isComposing) return;
                                    e.preventDefault();
                                    if (assistantLoading || !String(assistantPrompt || "").trim()) return;
                                    onAssistantSend();
                                }}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                    },
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={onAssistantSend}
                                disabled={
                                    assistantInputLocked ||
                                    assistantCreditsDepleted ||
                                    assistantLoading ||
                                    !String(assistantPrompt || "").trim()
                                }
                                sx={{
                                    flexShrink: 0,
                                    alignSelf: 'center',
                                    textTransform: 'none',
                                    backgroundColor: '#3BA666',
                                    '&:hover': { backgroundColor: '#2E8B57' },
                                    '&.Mui-disabled': {
                                        backgroundColor: 'rgba(59, 166, 102, 0.45)',
                                        color: 'rgba(255, 255, 255, 0.85)',
                                    },
                                }}
                            >
                                {t('puzzle.playCross.assistant-send', 'Send')}
                            </Button>
                        </Box>
                        {assistantLoading || assistantHistoryLoading ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    gap: 1,
                                    mt: 0.75,
                                }}
                            >
                                <CircularProgress size={22} />
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            ) : showWordNetwork && hasWordNetwork ? (
                <Box sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 0
                }}>
                    <WordNetworkPanel
                        wordNetwork={wordNetwork}
                        onToggle={() => setShowWordNetwork(false)}
                        onNodeSelect={(label) => {
                            if (onWordSelect && label) {
                                onWordSelect(label);
                            }
                        }}
                        hideVisibilityToggle
                        hideMostConnected
                        hideNodeEdgeCount
                    />
                </Box>
            ) : (
                <>
                    {storySlot ? (
                        <Box
                            sx={{
                                flexShrink: 0,
                                px: 1,
                                pt: 1,
                                pb: 0,
                                minWidth: 0,
                                "& .MuiAccordionDetails-root": {
                                    maxHeight: { xs: 200, md: 280 },
                                    overflowY: "auto",
                                },
                            }}
                        >
                            {storySlot}
                        </Box>
                    ) : null}
                    {/* Words Lists — Across / Down */}
                    <Box sx={{
                        flex: 1,
                        overflow: 'auto',
                        p: 1.25,
                        pt: storySlot ? 1 : 1.25,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0
                    }}>
                        <Box
                            sx={{
                                flex: 1,
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(0, 1fr)' },
                                gap: { xs: 1.25, sm: 2 },
                                alignItems: 'start',
                                minHeight: 0,
                                minWidth: 0,
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                {renderWordList(
                                    maxWords != null ? acrossWords.slice(0, Math.ceil(maxWords / 2)) : acrossWords,
                                    'Across'
                                )}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                {renderWordList(
                                    maxWords != null ? downWords.slice(0, Math.floor(maxWords / 2)) : downWords,
                                    'Down'
                                )}
                            </Box>

                            {acrossWords.length === 0 && downWords.length === 0 && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ textAlign: 'center', mt: 4, gridColumn: '1 / -1' }}
                                >
                                    No words available
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </>
            )}

            {/* Word Meaning Dialog */}
            <WordMeaningDialog
                open={dialogOpen}
                onClose={handleDialogClose}
                word={dialogWord}
                hideWord={true}
                language={language}
                uiLang={uiLang}
            />

            {/* Enter word mini popover */}
            <Popover
                open={Boolean(enterPopoverAnchor)}
                anchorEl={enterPopoverAnchor}
                onClose={handleEnterPopoverClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ sx: { p: 1.5, minWidth: 220 } }}
                TransitionProps={{
                    onEntered: () => {
                        const el = enterWordInputRef.current;
                        if (el) {
                            el.focus({ preventScroll: true });
                        }
                    }
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {enterPopoverNumber != null && (
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    minWidth: 28,
                                    minHeight: 28,
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "#C4FFE0",
                                    borderRadius: "50%",
                                    border: "1px solid #2E8B57",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    color: "#000",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)"
                                }}
                            >
                                {enterPopoverNumber}
                            </Box>
                        )}
                        <TextField
                            size="small"
                            inputRef={enterWordInputRef}
                            placeholder={
                                enterPopoverWord
                                    ? t("puzzle.playCross.enter-word", { count: removeHyphens(enterPopoverWord).length })
                                    : "Enter word"
                            }
                            value={localEnterPopoverValue}
                            inputProps={{
                                maxLength: enterPopoverWord ? removeHyphens(enterPopoverWord).length : 12,
                                style: {
                                    textAlign: "center",
                                    fontSize: "1.1rem",
                                    fontWeight: 600
                                }
                            }}
                            onChange={(e) => {
                                const v = e.target.value || "";
                                setLocalEnterPopoverValue(v);
                                debouncedSetEnterPopoverValue(v);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleEnterPopoverCheck();
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" sx={{ mr: 0.5 }}>
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 22,
                                                height: 22,
                                                borderRadius: '50%',
                                                backgroundColor: '#e8f5e9',
                                                border: '1px solid #2E8B57',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                color: '#2E8B57'
                                            }}
                                        >
                                            {localEnterPopoverValue.length}
                                        </Box>
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                "& .MuiOutlinedInput-root": {
                                    backgroundColor: SURFACE.sunken,
                                    "&:hover": {
                                        backgroundColor: SURFACE.card
                                    }
                                },
                                "& .MuiInputBase-input::placeholder": {
                                    fontSize: "0.875rem",
                                    fontWeight: 400,
                                    opacity: 0.6,
                                    textTransform: "none"
                                }
                            }}
                        />
                        <Tooltip title="Submit" arrow>
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={handleEnterPopoverCheck}
                                sx={{ flexShrink: 0 }}
                            >
                                <CheckIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    {enterPopoverHintPattern && hintPatternHasRevealedLetter(enterPopoverHintPattern) && (
                        <Box sx={{ pl: enterPopoverNumber != null ? 4.5 : 0, width: "100%" }}>
                            <HintPatternBoxes pattern={enterPopoverHintPattern} boxSize={18} />
                        </Box>
                    )}
                </Box>
            </Popover>

            {/* Subscription Dialog */}
            <SubscriptionDialog
                open={subscriptionDialogOpen}
                onClose={() => setSubscriptionDialogOpen(false)}
                triggerReason="general"
                userSubscription={UtilService.isStandardPayPlan(userPayPlan) ? 'premium' : 'free'}
            />

            <LoginConfirmDialog
                open={networkLoginDialogOpen}
                onClose={() => setNetworkLoginDialogOpen(false)}
                onConfirm={() => navigate('/login')}
                title={t("puzzle.playGrid.login-required")}
                message={t("puzzle.playGrid.login-network-msg")}
            />
        </Paper>
    );
};

export default CrossWordCluesPanel;

